"use client";

import { useEffect, useMemo, useState } from "react";
import { Check, LoaderCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useSubmitPollVote } from "@/hooks/use-feed";
import type { FeedPoll } from "@/lib/feed/types";
import { cn, getErrorMessage } from "@/lib/utils";

export function PollCard({
  postId,
  poll,
  enabled,
}: {
  postId: string;
  poll: FeedPoll;
  enabled: boolean;
}) {
  const submitVote = useSubmitPollVote(postId);
  const [selectedIds, setSelectedIds] = useState(poll.selectedOptionIds);
  const [otherSelected, setOtherSelected] = useState(
    Boolean(poll.selectedOtherText),
  );
  const [otherText, setOtherText] = useState(poll.selectedOtherText ?? "");
  const [error, setError] = useState<string | null>(null);
  const hasVoted =
    poll.selectedOptionIds.length > 0 || Boolean(poll.selectedOtherText);

  useEffect(() => {
    setSelectedIds(poll.selectedOptionIds);
    setOtherSelected(Boolean(poll.selectedOtherText));
    setOtherText(poll.selectedOtherText ?? "");
  }, [poll.selectedOptionIds, poll.selectedOtherText]);

  const resultRows = useMemo(
    () => [
      ...poll.options.map((option) => ({
        id: option.id,
        label: option.label,
        count: poll.optionCounts[option.id] ?? 0,
        selected: selectedIds.includes(option.id),
      })),
      ...(poll.allowOther
        ? [
            {
              id: "other",
              label: "Egyéb",
              count: poll.otherCount,
              selected: otherSelected,
            },
          ]
        : []),
    ],
    [otherSelected, poll, selectedIds],
  );

  function selectOption(id: string) {
    setError(null);
    if (poll.allowMultiple) {
      setSelectedIds((current) =>
        current.includes(id)
          ? current.filter((item) => item !== id)
          : [...current, id],
      );
      return;
    }

    setSelectedIds([id]);
    setOtherSelected(false);
    setOtherText("");
  }

  function toggleOther() {
    setError(null);
    if (poll.allowMultiple) {
      setOtherSelected((current) => !current);
      return;
    }

    setSelectedIds([]);
    setOtherSelected(true);
  }

  async function handleVote() {
    const trimmedOther = otherSelected ? otherText.trim() : "";
    if (selectedIds.length === 0 && !trimmedOther) {
      setError("Válasszon egy lehetőséget, vagy írjon be egyéb választ.");
      return;
    }

    setError(null);
    try {
      await submitVote.mutateAsync({
        optionIds: selectedIds,
        otherText: trimmedOther || null,
      });
    } catch (voteError) {
      setError(getErrorMessage(voteError));
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-sky-100 bg-sky-50/35 p-4">
      <div className="space-y-2">
        {resultRows.map((row) => {
          const percentage =
            poll.totalVoters > 0
              ? Math.round((row.count / poll.totalVoters) * 100)
              : 0;
          const isOther = row.id === "other";

          return (
            <div key={row.id}>
              <button
                type="button"
                disabled={!enabled || submitVote.isPending}
                className={cn(
                  "relative flex min-h-11 w-full items-center gap-3 overflow-hidden rounded-lg border bg-white px-3 py-2 text-left transition",
                  row.selected
                    ? "border-sky-300 ring-2 ring-sky-100"
                    : "border-slate-200 hover:border-sky-200",
                )}
                onClick={() =>
                  isOther ? toggleOther() : selectOption(row.id)
                }
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 bg-sky-100/70 transition-[width]"
                  style={{ width: `${Math.min(100, percentage)}%` }}
                />
                <span
                  className={cn(
                    "relative flex size-4 shrink-0 items-center justify-center border",
                    poll.allowMultiple ? "rounded" : "rounded-full",
                    row.selected
                      ? "border-primary bg-primary text-white"
                      : "border-slate-300 bg-white",
                  )}
                >
                  {row.selected ? <Check className="size-3" /> : null}
                </span>
                <span className="relative min-w-0 flex-1 text-sm font-medium text-slate-800">
                  {row.label}
                </span>
                <span className="relative text-xs font-semibold tabular-nums text-slate-500">
                  {percentage}%
                </span>
              </button>

              {isOther && otherSelected ? (
                <input
                  type="text"
                  value={otherText}
                  maxLength={120}
                  autoFocus
                  placeholder="Írja be a saját válaszát…"
                  className="mt-2 h-9 w-full rounded-lg border border-sky-200 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-sky-100"
                  onChange={(event) => setOtherText(event.target.value)}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {poll.totalVoters} szavazó
          {poll.allowMultiple ? " · Több válasz is jelölhető" : ""}
        </p>
        <Button
          type="button"
          size="sm"
          className="rounded-full"
          disabled={!enabled || submitVote.isPending}
          onClick={() => void handleVote()}
        >
          {submitVote.isPending ? (
            <LoaderCircle className="size-3.5 animate-spin" />
          ) : null}
          {hasVoted ? "Szavazat módosítása" : "Szavazat leadása"}
        </Button>
      </div>

      {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
